import { zodResolver } from "@hookform/resolvers/zod";
import { t, Trans } from "@lingui/macro";
import { CircleNotch, Envelope, Info, LockKeyOpenIcon, Star } from "@phosphor-icons/react";
import { registerSchema } from "@stylingcv/dto";
import {
  Alert,
  AlertTitle,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  PasswordInput,
} from "@stylingcv/ui";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { LocaleLink } from "@/client/components/locale-link";
import { useToast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { SocialAuth } from "@/client/pages/auth/_components/social-auth";
import { useRegister } from "@/client/services/auth";
import { useAuthProviders } from "@/client/services/auth/providers";
import { useFeatureFlags } from "@/client/services/feature";
import { trackSignUp } from "@/client/utils/gtm";

type FormValues = z.infer<typeof registerSchema>;

const deriveFromEmail = (email: string) => {
  const localPart = email.split("@")[0] || "";

  const name = localPart
    .replace(/[._-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || "User";

  const baseUsername = localPart
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "")
    .slice(0, 24) || "user";

  const randomSuffix = Math.random().toString(36).slice(2, 6);
  const username = `${baseUsername}-${randomSuffix}`;

  return { name, username };
};

type SignupFormProps = {
  onSuccess: (data: { email: string; name: string }) => void | Promise<void>;
  prefilledEmail?: string;
  showHeader?: boolean;
  showFooterLinks?: boolean;
  showSocialAuth?: boolean;
  showTrustBadge?: boolean;
  submitLabel?: string;
  className?: string;
};

export const SignupForm = ({
  onSuccess,
  prefilledEmail = "",
  showHeader = false,
  showFooterLinks = true,
  showSocialAuth = true,
  showTrustBadge = false,
  submitLabel,
  className,
}: SignupFormProps) => {
  const { providers, loading: providersLoading } = useAuthProviders();
  const { flags } = useFeatureFlags();
  const { registerWithErrorParsing, loading } = useRegister();
  const { toast } = useToast();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      username: "",
      email: prefilledEmail,
      password: "",
      locale: "en-US",
    },
  });

  const { watch, setValue } = form;
  const email = watch("email");

  useEffect(() => {
    if (email && email.includes("@")) {
      const { name, username } = deriveFromEmail(email);
      setValue("name", name, { shouldValidate: false });
      setValue("username", username, { shouldValidate: false });
    }
  }, [email, setValue]);

  useEffect(() => {
    if (prefilledEmail) {
      form.setValue("email", prefilledEmail);
    }
  }, [prefilledEmail, form]);

  const effectiveProviders = providers ?? [];
  const hasEmailAuth = effectiveProviders.includes("email") || effectiveProviders.length === 0;
  const hasSocialAuth = showSocialAuth && effectiveProviders.some((p) => p !== "email");
  const showDivider = hasEmailAuth && hasSocialAuth;

  const onSubmit = async (data: FormValues) => {
    form.clearErrors();
    setFormError(null);

    if (!data.name || !data.username) {
      const { name, username } = deriveFromEmail(data.email);
      data.name = data.name || name;
      data.username = data.username || username;
    }

    const result = await registerWithErrorParsing(data);

    if (result.success) {
      trackSignUp({ method: "email" });

      toast({
        variant: "info",
        icon: <Info size={16} weight="bold" />,
        title: t`Email Verification`,
        description: t`You should have received an email from Stylion with a link to verify your account.`,
      });

      await onSuccess({ email: data.email, name: data.name });
    } else {
      if (result.fieldErrors) {
        const errorMessage = Object.values(result.fieldErrors)[0];
        if (errorMessage) {
          setFormError(errorMessage);
        }
      } else {
        setFormError((result as any).error || t`Network error. Please check your connection and try again.`);
      }
    }
  };

  return (
    <div className={className}>
      {showHeader && (
        <div className="mb-4 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            <Trans>Create Your <span className="text-teal-600 dark:text-teal-400">Free</span> Account</Trans>
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t`Save your resume and unlock all features`}
          </p>
          {showTrustBadge && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <Star className="size-3 fill-current" weight="fill" />
              {t`Trusted by 6M+ professionals`}
            </div>
          )}
        </div>
      )}

      {flags.isSignupsDisabled && (
        <Alert variant="error" className="mb-4">
          <AlertTitle>{t`Signups are currently disabled by the administrator.`}</AlertTitle>
        </Alert>
      )}

      {showSocialAuth && (providersLoading || hasSocialAuth) && (
        <div className="mb-3">
          {providersLoading ? (
            <div className="flex flex-col gap-2.5">
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
            </div>
          ) : (
            <SocialAuth />
          )}
        </div>
      )}

      {(showDivider || (showSocialAuth && providersLoading)) && (
        <div className="my-4 flex items-center gap-3">
          <hr className="flex-1 border-gray-200 dark:border-gray-700" />
          <span className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {t`or`}
          </span>
          <hr className="flex-1 border-gray-200 dark:border-gray-700" />
        </div>
      )}

      {hasEmailAuth && (
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <Envelope className="absolute left-3.5 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
                      <Input
                        type="email"
                        placeholder={t`Enter your email address`}
                        autoComplete="off"
                        className="h-12 rounded-xl border border-gray-200 bg-white pl-11 pr-4 text-base lowercase placeholder:text-gray-400 focus:border-teal-500 focus:shadow-[0_1px_3px_0_rgba(38,133,153,0.25),0_1px_2px_-1px_rgba(38,133,153,0.15)] dark:border-gray-600 dark:bg-gray-800 dark:placeholder:text-gray-500 dark:focus:border-teal-400"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="relative">
                      <LockKeyOpenIcon className="pointer-events-none absolute left-3.5 top-1/2 z-10 size-5 -translate-y-1/2 text-gray-400" />
                      <PasswordInput
                        placeholder={t`Create a password`}
                        autoComplete="off"
                        className="h-12 rounded-xl border border-gray-200 bg-white pl-11 text-base placeholder:text-gray-400 focus:border-teal-500 focus:shadow-[0_1px_3px_0_rgba(38,133,153,0.25),0_1px_2px_-1px_rgba(38,133,153,0.15)] dark:border-gray-600 dark:bg-gray-800 dark:placeholder:text-gray-500 dark:focus:border-teal-400"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <input type="hidden" {...form.register("name")} />
            <input type="hidden" {...form.register("username")} />

            {formError && <p className="text-sm text-red-500">{formError}</p>}

            <Button
              type="submit"
              disabled={flags.isSignupsDisabled || loading}
              className="mt-1 h-12 w-full rounded-xl bg-teal-600 text-base font-semibold text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow-md dark:bg-teal-500 dark:hover:bg-teal-600"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <CircleNotch className="size-4 animate-spin" />
                  {t`Creating account...`}
                </span>
              ) : (
                submitLabel || t`Create Free Account`
              )}
            </Button>
          </form>
        </Form>
      )}

      {showFooterLinks && (
        <div className="mt-5 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <Trans>Already have an account? </Trans>
            <Button
              asChild
              variant="link"
              className="h-auto p-0 text-sm font-semibold text-primary hover:text-primary/80 hover:underline"
            >
              <LocaleLink to="/auth/login">{t`Log In`}</LocaleLink>
            </Button>
          </p>
        </div>
      )}
    </div>
  );
};
